import type { Lang } from '@/mocks/sections';
import { articles } from '@/mocks/articles';

export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cssbuybr.ink';
export const SITE_NAME = 'CSSBuyBR.INK';
export const SITE_LOGO =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';
export const HOME_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Premium%20streetwear%20fashion%20editorial%20photography%20with%20sneakers%20hoodies%20and%20accessories%20arranged%20diagonally%20on%20a%20warm%20cream%20studio%20backdrop%2C%20soft%20golden%20lighting%20with%20gentle%20shadows%2C%20high-end%20e-commerce%20hero%20composition%20with%20generous%20negative%20space%2C%20vibrant%20yet%20harmonious%20color%20palette&width=1600&height=900&seq=hero-bg&orientation=landscape&nocache=true';

export interface ArticleLike {
  slug: string;
  title: string;
  description: string;
  image: string;
  published: string;
  updated: string;
  author: string;
  tags: string[];
}

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  ogImage: string;
  ogLocale: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
}

export interface ResolvedSeo {
  route: 'home' | 'article' | 'notfound';
  meta: SeoMeta | null;
  article?: ArticleLike;
}

const LOCALES: Record<Lang, string> = {
  pt: 'pt_BR',
  en: 'en_US',
  de: 'de_DE',
};

const HOME_SEO: Record<Lang, SeoMeta> = {
  pt: {
    title: 'CSSBuy Spreadsheet: Produtos, Vendedores & Guias de Compra | CSSBuyBR.INK',
    description:
      'Explore o CSSBuy Spreadsheet, descubra produtos e vendedores, compare opções e aprenda como avaliar QC, armazenamento, envio e informações importantes antes de realizar uma compra. Guia independente em português, inglês e alemão.',
    keywords:
      'cssbuy spreadsheet, cssbuy review, cssbuy shipping cost, cssbuy coupon, cssbuy discount code, cssbuy warehouse, cssbuy legit, cssbuy guide, cssbuy products, cssbuy sellers, cssbuy QC, cssbuy compras',
    canonical: `${SITE_URL}/`,
    ogType: 'website',
    ogTitle: 'CSSBuy Spreadsheet: Produtos, Vendedores & Guias de Compra',
    ogDescription:
      'Explore o CSSBuy Spreadsheet, descubra produtos e vendedores, compare opções e aprenda como avaliar QC, envio e informações antes de comprar.',
    ogUrl: `${SITE_URL}/`,
    ogImage: HOME_OG_IMAGE,
    ogLocale: 'pt_BR',
    twitterTitle: 'CSSBuy Spreadsheet: Produtos, Vendedores & Guias de Compra',
    twitterDescription:
      'Explore o CSSBuy Spreadsheet, descubra produtos e vendedores, compare opções e aprenda como avaliar QC, envio e informações antes de comprar.',
    twitterImage: HOME_OG_IMAGE,
  },
  en: {
    title: 'CSSBuy Spreadsheet: Products, Sellers & Buying Guides | CSSBuyBR.INK',
    description:
      'Explore the CSSBuy Spreadsheet, discover products and sellers, compare options and learn how to evaluate QC, warehousing, shipping and important information before making a purchase. Independent guide for international buyers.',
    keywords:
      'cssbuy spreadsheet, cssbuy review, cssbuy shipping cost, cssbuy coupon, cssbuy discount code, cssbuy warehouse, cssbuy legit, cssbuy guide, cssbuy products, cssbuy sellers, cssbuy QC, buying guide',
    canonical: `${SITE_URL}/en`,
    ogType: 'website',
    ogTitle: 'CSSBuy Spreadsheet: Products, Sellers & Buying Guides',
    ogDescription:
      'Explore the CSSBuy Spreadsheet, discover products and sellers, compare options and learn how to evaluate QC, shipping and information before buying.',
    ogUrl: `${SITE_URL}/en`,
    ogImage: HOME_OG_IMAGE,
    ogLocale: 'en_US',
    twitterTitle: 'CSSBuy Spreadsheet: Products, Sellers & Buying Guides',
    twitterDescription:
      'Explore the CSSBuy Spreadsheet, discover products and sellers, compare options and learn how to evaluate QC, shipping and information before buying.',
    twitterImage: HOME_OG_IMAGE,
  },
  de: {
    title: 'CSSBuy Spreadsheet: Produkte, Verkäufer & Kaufanleitungen | CSSBuyBR.INK',
    description:
      'Erkunden Sie das CSSBuy Spreadsheet, entdecken Sie Produkte und Verkäufer, vergleichen Sie Optionen und lernen Sie, wie Sie QC, Lagerung, Versand und wichtige Informationen vor dem Kauf bewerten. Unabhängiger Leitfaden für Käufer.',
    keywords:
      'cssbuy spreadsheet, cssbuy review, cssbuy versandkosten, cssbuy coupon, cssbuy discount code, cssbuy warehouse, cssbuy legit, cssbuy leitfaden, cssbuy produkte, cssbuy verkäufer, cssbuy QC',
    canonical: `${SITE_URL}/de`,
    ogType: 'website',
    ogTitle: 'CSSBuy Spreadsheet: Produkte, Verkäufer & Kaufanleitungen',
    ogDescription:
      'Erkunden Sie das CSSBuy Spreadsheet, entdecken Sie Produkte und Verkäufer, vergleichen Sie Optionen und lernen Sie, wie Sie QC, Versand und Informationen bewerten.',
    ogUrl: `${SITE_URL}/de`,
    ogImage: HOME_OG_IMAGE,
    ogLocale: 'de_DE',
    twitterTitle: 'CSSBuy Spreadsheet: Produkte, Verkäufer & Kaufanleitungen',
    twitterDescription:
      'Erkunden Sie das CSSBuy Spreadsheet, entdecken Sie Produkte und Verkäufer, vergleichen Sie Optionen und lernen Sie, wie Sie QC, Versand und Informationen bewerten.',
    twitterImage: HOME_OG_IMAGE,
  },
};

function buildArticleMeta(article: ArticleLike, lang: Lang): SeoMeta {
  const url = `${SITE_URL}/artigo/${article.slug}`;
  const locale = LOCALES[lang];
  const keywords = [...article.tags, 'cssbuy spreadsheet'].join(', ');
  return {
    title: `${article.title} | CSSBuyBR.INK`,
    description: article.description,
    keywords,
    canonical: url,
    ogType: 'article',
    ogTitle: article.title,
    ogDescription: article.description,
    ogUrl: url,
    ogImage: article.image,
    ogLocale: locale,
    twitterTitle: article.title,
    twitterDescription: article.description,
    twitterImage: article.image,
  };
}

export function resolveSeo(pathname: string, lang: Lang): ResolvedSeo {
  const trimmed = pathname.replace(/\/+$/, '') || '/';

  if (trimmed === '/' || trimmed === '/en' || trimmed === '/de') {
    return { route: 'home', meta: { ...HOME_SEO[lang] } };
  }

  const match = trimmed.match(/^\/artigo\/([^/]+)/);
  if (match) {
    const slug = match[1];
    const langArticles = articles[lang] || articles.pt;
    const article: ArticleLike =
      langArticles.find((a) => a.slug === slug) || langArticles[0];
    return { route: 'article', meta: buildArticleMeta(article, lang), article };
  }

  return { route: 'notfound', meta: null };
}

function ensureMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function ensureLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeMeta(attr: 'name' | 'property', key: string) {
  document.head
    .querySelectorAll(`meta[${attr}="${key}"]`)
    .forEach((el) => el.remove());
}

function removeLink(rel: string) {
  document.head
    .querySelectorAll(`link[rel="${rel}"]`)
    .forEach((el) => el.remove());
}

export function applySeoMeta(meta: SeoMeta) {
  document.title = meta.title;
  ensureMeta('name', 'description', meta.description);
  ensureMeta('name', 'keywords', meta.keywords);
  ensureMeta('name', 'robots', 'index, follow');
  ensureLink('canonical', meta.canonical);
  ensureMeta('property', 'og:title', meta.ogTitle);
  ensureMeta('property', 'og:description', meta.ogDescription);
  ensureMeta('property', 'og:url', meta.ogUrl);
  ensureMeta('property', 'og:type', meta.ogType);
  ensureMeta('property', 'og:image', meta.ogImage);
  ensureMeta('property', 'og:locale', meta.ogLocale);
  ensureMeta('name', 'twitter:title', meta.twitterTitle);
  ensureMeta('name', 'twitter:description', meta.twitterDescription);
  ensureMeta('name', 'twitter:image', meta.twitterImage);
}

export function applyNoindex() {
  document.title = 'Página não encontrada | CSSBuyBR.INK';
  ensureMeta('name', 'robots', 'noindex, nofollow');
  ensureMeta('name', 'description', 'A página que você procura não existe ou foi movida.');
  removeMeta('name', 'keywords');
  removeLink('canonical');
  removeMeta('property', 'og:title');
  removeMeta('property', 'og:description');
  removeMeta('property', 'og:url');
  removeMeta('property', 'og:image');
  removeMeta('name', 'twitter:title');
  removeMeta('name', 'twitter:description');
  removeMeta('name', 'twitter:image');
}